import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import axiosAdminInstance from "../../api/adminAxios";

interface Restaurant {
    id: number;
    name: string;
    email: string;
    subscription_id: number;
}

export default function ManageRestaurants() {
    const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ id: 0, name: "", email: "", subscription_id: 0 });
    const [isEditMode, setIsEditMode] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchRestaurants();
    }, []);

    const fetchRestaurants = async () => {
        try {
            const response = await axiosAdminInstance.get<Restaurant[]>("/admin/restaurants");
            setRestaurants(response.data);
        } catch (err) {
            console.error("Failed to fetch restaurants:", err);
            navigate("/admin/login");
        }
    };
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const totalPages = Math.ceil(restaurants.length / itemsPerPage);
    const paginatedRestaurants = restaurants.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );


    const openModal = (restaurant?: Restaurant) => {
        if (restaurant) {
            setIsEditMode(true);
            setFormData(restaurant);
        } else {
            setIsEditMode(false);
            setFormData({ id: 0, name: "", email: "", subscription_id: 0 });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormData({ id: 0, name: "", email: "", subscription_id: 0 });
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (isEditMode) {
                await axiosAdminInstance.put(`/admin/restaurants/${formData.id}`, formData);
            } else {
                await axiosAdminInstance.post("/admin/restaurants", formData);
            }
            closeModal();
            fetchRestaurants();
        } catch (err) {
            console.error("Error saving restaurant:", err);
        }
    };

    const handleDelete = async (id: number) => {
        const confirmed = window.confirm("Are you sure you want to delete this restaurant?");
        if (!confirmed) return;

        try {
            await axiosAdminInstance.delete(`/admin/restaurants/${id}`);
            fetchRestaurants();
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    return (
        <div className="min-h-screen flex">
            <AdminSidebar />

            <div className="flex-1 p-6 bg-gray-100">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Manage Restaurants</h2>
                    <button
                        onClick={() => openModal()}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        ➕ Add Restaurant
                    </button>
                </div>

                <ul className="space-y-4">
                    {paginatedRestaurants.map((restaurant) => (
                        <li
                            key={restaurant.id}
                            className="border p-4 rounded shadow bg-white flex justify-between items-center"
                        >
                            <div>
                                <h3 className="text-lg font-bold">{restaurant.name}</h3>
                                <p>{restaurant.email}</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="text-yellow-500 hover:text-yellow-600"
                                    onClick={() => openModal(restaurant)}
                                >
                                    ✏️
                                </button>
                                <button
                                    className="text-red-500 hover:text-red-600"
                                    onClick={() => handleDelete(restaurant.id)}
                                >
                                    🗑️
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
                <div className="flex justify-between items-center mt-6 text-sm text-gray-600">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-200' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                        ◀ Prev
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-200' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                        Next ▶
                    </button>
                </div>
            </div>
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
                        <h3 className="text-lg font-bold mb-4">{isEditMode ? "Edit" : "Add"} Restaurant</h3>
                        <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Subscription ID</label>
                                <input
                                    type="number"
                                    value={formData.subscription_id}
                                    onChange={(e) => setFormData({ ...formData, subscription_id: parseInt(e.target.value) })}
                                    className="w-full p-2 border rounded"
                                />
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
